import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { BookmarkCheck } from "lucide-react"
import { api } from "@/lib/api"
import { formatRelative } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type BookmarkRow = {
  id: string
  paper_id: string
  note: string | null
  created_at: string
  paper_title_ko: string | null
  paper_title_en: string
  arxiv_id: string | null
}

export default function BookmarksPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ["me-bookmarks"],
    queryFn: () => api.get<BookmarkRow[]>("/bookmarks?limit=100"),
  })
  const remove = useMutation({
    mutationFn: (paperId: string) =>
      api.delete<void>(`/bookmarks/by-paper/${paperId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me-bookmarks"] })
      qc.invalidateQueries({ queryKey: ["me-bookmark-ids"] })
    },
  })

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">북마크</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          저장한 논문을 한 곳에서 확인합니다.
        </p>
      </header>

      {isLoading && (
        <div className="text-sm text-muted-foreground">불러오는 중...</div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-md border border-border p-8 text-center text-sm text-muted-foreground">
          아직 북마크한 논문이 없습니다. 논문 카드의 북마크 아이콘으로 추가하세요.
        </div>
      )}

      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((b) => {
            const title = b.paper_title_ko || b.paper_title_en
            return (
              <li
                key={b.id}
                className="rounded-lg border border-border bg-card p-4 transition-shadow hover:shadow-sm"
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <BookmarkCheck className="h-3.5 w-3.5 text-primary" />
                  <span>{formatRelative(b.created_at)}</span>
                  {b.arxiv_id && <span>· arXiv:{b.arxiv_id}</span>}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-7 px-2 text-xs"
                    onClick={() => remove.mutate(b.paper_id)}
                  >
                    해제
                  </Button>
                </div>
                <Link
                  to={`/papers/${b.paper_id}`}
                  className="block text-base font-medium hover:text-primary"
                >
                  {title}
                </Link>
                {b.note && (
                  <p className="mt-1 text-sm text-muted-foreground">{b.note}</p>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
